import { ResizableNodeView } from "@tiptap/core";
import Image from "@tiptap/extension-image";

import { editorT } from "../lib/i18n";

const getPercentageWidth = (value: unknown) => {
  if (typeof value !== "string" || !value.endsWith("%")) return null;
  const percentage = Number(value.slice(0, -1));
  return Number.isFinite(percentage) && percentage > 0 && percentage <= 100
    ? `${percentage}%`
    : null;
};

const parseDimensionAttribute = (element: HTMLElement, name: "height" | "width") => {
  const value = element.getAttribute(name) ?? element.getAttribute(`data-${name}`);
  if (name === "width") {
    const percentage = getPercentageWidth(value);
    if (percentage) return percentage;
  }
  const parsed = value ? Number.parseInt(value, 10) : Number.NaN;

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getDimensionAttribute = (attributes: Record<string, unknown>, name: "height" | "width") => {
  const value = attributes[name];
  if (name === "width") {
    const percentage = getPercentageWidth(value);
    if (percentage) return percentage;
  }

  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }

  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);

    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

const syncImageAttributes = (element: HTMLImageElement, attributes: Record<string, unknown>) => {
  Array.from(element.getAttributeNames()).forEach((name) => {
    if (name !== "src") {
      element.removeAttribute(name);
    }
  });

  Object.entries(attributes).forEach(([key, value]) => {
    if (value == null || key === "width" || key === "height") {
      return;
    }

    if (key === "src") {
      element.src = String(value);
      return;
    }

    element.setAttribute(key, String(value));
  });
};

const applyImageSizeStyles = (element: HTMLImageElement, attributes: Record<string, unknown>) => {
  const width = getDimensionAttribute(attributes, "width");
  const height = getDimensionAttribute(attributes, "height");
  const percentage = typeof width === "string";
  if (element.parentElement) {
    if (percentage) element.parentElement.style.width = width;
    else element.parentElement.style.removeProperty("width");
  }

  if (width) {
    element.style.width = percentage ? "100%" : `${width}px`;
  } else {
    element.style.removeProperty("width");
  }

  if (percentage) {
    element.style.height = "auto";
  } else if (height) {
    element.style.height = `${height}px`;
  } else {
    element.style.removeProperty("height");
  }
};

const getImageAspectRatio = (imageElement: HTMLImageElement, width: number, height: number) => {
  if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
    return imageElement.naturalWidth / imageElement.naturalHeight;
  }

  if (width > 0 && height > 0) {
    return width / height;
  }

  return 1;
};

const getResizeContainerMaxWidth = (container: HTMLElement) => {
  const width = container.getBoundingClientRect().width;

  if (width > 0) {
    return width;
  }

  const parentWidth = container.parentElement?.getBoundingClientRect().width ?? 0;

  return parentWidth > 0 ? parentWidth : Number.POSITIVE_INFINITY;
};

const constrainImageSize = (
  imageElement: HTMLImageElement,
  container: HTMLElement,
  width: number,
  height: number,
  minWidth: number,
  minHeight: number,
) => {
  const aspectRatio = getImageAspectRatio(imageElement, width, height);
  const maxWidth = getResizeContainerMaxWidth(container);
  let constrainedWidth = Math.max(minWidth, width);
  let constrainedHeight = constrainedWidth / aspectRatio;

  if (constrainedHeight < minHeight) {
    constrainedHeight = minHeight;
    constrainedWidth = constrainedHeight * aspectRatio;
  }

  if (Number.isFinite(maxWidth) && constrainedWidth > maxWidth) {
    constrainedWidth = maxWidth;
    constrainedHeight = constrainedWidth / aspectRatio;
  }

  return {
    width: Math.round(constrainedWidth),
    height: Math.round(constrainedHeight),
  };
};

const getImageLoadErrorElement = (container: HTMLElement) => {
  const existingElement = container.querySelector<HTMLElement>("[data-image-load-error]");

  if (existingElement) {
    return existingElement;
  }

  const errorElement = document.createElement("div");
  errorElement.setAttribute("data-image-load-error", "true");

  const iconElement = document.createElement("span");
  iconElement.setAttribute("aria-hidden", "true");
  iconElement.setAttribute("data-image-load-error-icon", "true");

  const textElement = document.createElement("span");
  textElement.textContent = editorT("image.loadFailed");

  errorElement.append(iconElement, textElement);
  container.appendChild(errorElement);

  return errorElement;
};

type ImageAlign = "left" | "center" | "right";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editorImage: {
      setImageAlign: (align: ImageAlign) => ReturnType;
      fitImageToWidth: () => ReturnType;
      resetImageSize: () => ReturnType;
    };
  }
}

export const EditorImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") ?? "center",
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
      },
      width: {
        default: null,
        parseHTML: (element) => parseDimensionAttribute(element, "width"),
        renderHTML: (attributes) => {
          const percentage = getPercentageWidth(attributes.width);
          return percentage
            ? { "data-width": percentage, style: `width:${percentage};height:auto` }
            : attributes.width ? { width: attributes.width, "data-width": attributes.width } : {};
        },
      },
      height: {
        default: null,
        parseHTML: (element) => parseDimensionAttribute(element, "height"),
        renderHTML: (attributes) =>
          attributes.height ? { height: attributes.height, "data-height": attributes.height } : {},
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageAlign:
        (align: ImageAlign) =>
        ({ commands }: { commands: { updateAttributes: (typeOrName: string, attributes: Record<string, unknown>) => boolean } }) =>
          commands.updateAttributes(this.name, { align }),
      fitImageToWidth:
        () =>
        ({ commands }: { commands: { updateAttributes: (typeOrName: string, attributes: Record<string, unknown>) => boolean } }) =>
          commands.updateAttributes(this.name, { width: "100%", height: null }),
      resetImageSize:
        () =>
        ({ commands }: { commands: { updateAttributes: (typeOrName: string, attributes: Record<string, unknown>) => boolean } }) =>
          commands.updateAttributes(this.name, {
            width: null,
            height: null,
          }),
    };
  },

  addNodeView() {
    if (!this.options.resize || !this.options.resize.enabled || typeof document === "undefined") {
      return null;
    }

    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } = this.options.resize;
    const resizeMinWidth = minWidth ?? 8;
    const resizeMinHeight = minHeight ?? 8;

    return ({ node, getPos, HTMLAttributes }) => {
      const imageElement = document.createElement("img");
      let imageLoadFailed = false;

      const nodeView: ResizableNodeView = new ResizableNodeView({
        element: imageElement,
        editor: this.editor,
        node,
        getPos,
        onResize: (width, height) => {
          const constrained = constrainImageSize(imageElement, nodeView.dom, width, height, resizeMinWidth, resizeMinHeight);

          imageElement.parentElement?.style.removeProperty("width");
          imageElement.style.width = `${constrained.width}px`;
          imageElement.style.height = `${constrained.height}px`;
        },
        onCommit: (width, height) => {
          const pos = getPos();

          if (pos === undefined) {
            return;
          }

          const constrained = constrainImageSize(imageElement, nodeView.dom, width, height, resizeMinWidth, resizeMinHeight);

          this.editor.chain().setNodeSelection(pos).updateAttributes(this.name, constrained).run();
        },
        onUpdate: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false;
          }

          const attrs = updatedNode.attrs as Record<string, unknown>;
          const previousSrc = imageElement.getAttribute("src");
          const nextSrc = attrs.src == null ? null : String(attrs.src);

          if (previousSrc !== nextSrc) {
            imageLoadFailed = false;
            nodeView.dom.removeAttribute("data-image-load-state");
            imageElement.style.removeProperty("display");
          }

          syncImageAttributes(imageElement, attrs);

          applyImageSizeStyles(imageElement, attrs);

          nodeView.dom.setAttribute("data-align", String(attrs.align ?? "center"));
          return true;
        },
        options: {
          directions,
          min: {
            width: resizeMinWidth,
            height: resizeMinHeight,
          },
          preserveAspectRatio: alwaysPreserveAspectRatio === true,
        },
      });

      const revealImage = () => {
        nodeView.dom.style.visibility = "";
        nodeView.dom.style.pointerEvents = "";
      };

      const showImage = () => {
        imageLoadFailed = false;
        nodeView.dom.setAttribute("data-image-load-state", "loaded");
        imageElement.style.removeProperty("display");
        revealImage();
      };

      const showImageLoadError = () => {
        imageLoadFailed = true;
        nodeView.dom.setAttribute("data-image-load-state", "error");
        imageElement.style.display = "none";
        getImageLoadErrorElement(nodeView.dom);
        revealImage();
      };

      nodeView.dom.setAttribute("data-align", String(node.attrs.align ?? "center"));
      nodeView.dom.style.visibility = "hidden";
      nodeView.dom.style.pointerEvents = "none";
      imageElement.onload = showImage;
      imageElement.onerror = showImageLoadError;
      syncImageAttributes(imageElement, HTMLAttributes);
      applyImageSizeStyles(imageElement, node.attrs as Record<string, unknown>);

      if (imageElement.complete) {
        if (imageElement.naturalWidth > 0 || imageLoadFailed) {
          showImage();
        } else {
          showImageLoadError();
        }
      }

      return nodeView;
    };
  },
});
